import { SafeResourceUrl } from '@angular/platform-browser';
import { DocumentDescriptionDTO } from '@iota/iota-management-portal-v4-client';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, createSelector, on } from '@ngrx/store';
import moment from 'moment';
import {
    DeleteCentralRegulatorLogo,
    DeleteCentralRegulatorLogoExtra,
    DeleteCentralRegulatorLogoExtraFailure,
    DeleteCentralRegulatorLogoExtraSuccess,
    DeleteCentralRegulatorLogoFailure,
    DeleteCentralRegulatorLogoSuccess,
    DeleteCompanyLogo,
    DeleteCompanyLogoExtra,
    DeleteCompanyLogoExtraFailure,
    DeleteCompanyLogoExtraSuccess,
    DeleteCompanyLogoFailure,
    DeleteCompanyLogoSuccess,
    DeleteHealthInsurerLogo,
    DeleteHealthInsurerLogoFailure,
    DeleteHealthInsurerLogoSuccess,
    LoadCentralRegulatorLogo,
    LoadCentralRegulatorLogoExtra,
    LoadCentralRegulatorLogoExtraFailure,
    LoadCentralRegulatorLogoExtraSuccess,
    LoadCentralRegulatorLogoFailure,
    LoadCentralRegulatorLogoSuccess,
    LoadCompanyLogo,
    LoadCompanyLogoExtra,
    LoadCompanyLogoExtraFailure,
    LoadCompanyLogoExtraSuccess,
    LoadCompanyLogoFailure,
    LoadCompanyLogoSuccess,
    LoadDocument,
    LoadDocumentFailure,
    LoadDocumentSuccess,
    LoadHealthInsurerLogo,
    LoadHealthInsurerLogoFailure,
    LoadHealthInsurerLogoSuccess,
    LoadProfilePicture,
    LoadProfilePictureFailure,
    LoadProfilePictureSuccess
} from '../actions/document.action';
import { AppState } from '../store';

export interface DocumentDescriptionWithUrl extends DocumentDescriptionDTO {
    url?: SafeResourceUrl;
}
function selectDocumentDescriptionId(a: DocumentDescriptionWithUrl): string {
    return a.documentDescriptionId;
}
function sortDocuments(a: DocumentDescriptionWithUrl, b: DocumentDescriptionWithUrl): number {
    return moment(b.creationDateTime).toDate().getTime() - moment(a.creationDateTime).toDate().getTime();
}

// Actual documents
interface DocumentFileState extends EntityState<DocumentDescriptionWithUrl> {
    error: any;
    loading: boolean;
}
const documentFileAdapter: EntityAdapter<DocumentDescriptionWithUrl> = createEntityAdapter<DocumentDescriptionWithUrl>({
    selectId: selectDocumentDescriptionId,
    sortComparer: sortDocuments
});
const documentFileInitialState: DocumentFileState = documentFileAdapter.getInitialState({
    error: undefined,
    loading: false
});
const { selectAll: selectAllDocuments } = documentFileAdapter.getSelectors();

export interface DocumentUrlWithKey {
    key: string;
    url: SafeResourceUrl;
    loading: boolean;
}
function selectKey(a: DocumentUrlWithKey): string {
    return a.key;
}
// Company logos
interface CompanyLogoState extends EntityState<DocumentUrlWithKey> {
    error: any;
    loading: boolean;
}
const companyLogoAdapter: EntityAdapter<DocumentUrlWithKey> = createEntityAdapter<DocumentUrlWithKey>({
    selectId: selectKey
});
const companyLogoInitialState: CompanyLogoState = companyLogoAdapter.getInitialState({
    error: undefined,
    loading: false
});
const { selectAll: selectAllCompanyLogos } = companyLogoAdapter.getSelectors();

interface CompanyLogoExtraState extends EntityState<DocumentUrlWithKey> {
    error: any;
    loading: boolean;
}
const companyLogoExtraAdapter: EntityAdapter<DocumentUrlWithKey> = createEntityAdapter<DocumentUrlWithKey>({
    selectId: selectKey
});
const companyLogoExtraInitialState: CompanyLogoExtraState = companyLogoAdapter.getInitialState({
    error: undefined,
    loading: false
});
const { selectAll: selectAllCompanyLogoExtras } = companyLogoExtraAdapter.getSelectors();

// health insurer logos
interface HealthInsurerLogoState extends EntityState<DocumentUrlWithKey> {
    error: any;
    loading: boolean;
}
const healthInsurerLogoAdapter: EntityAdapter<DocumentUrlWithKey> = createEntityAdapter<DocumentUrlWithKey>({
    selectId: selectKey
});
const healthInsurerLogoInitialState: CompanyLogoState = healthInsurerLogoAdapter.getInitialState({
    error: undefined,
    loading: false
});
const { selectAll: selectAllHealthInsurerLogos } = healthInsurerLogoAdapter.getSelectors();

// Profile Pictures
interface ProfilePictureState extends EntityState<DocumentUrlWithKey> {
    error: any;
    loading: boolean;
}
const profilePictureAdapter: EntityAdapter<DocumentUrlWithKey> = createEntityAdapter<DocumentUrlWithKey>({
    selectId: selectKey
});
const profilePictureInitialState: ProfilePictureState = profilePictureAdapter.getInitialState({
    error: undefined,
    loading: false
});
const { selectAll: selectAllProfilePictures } = profilePictureAdapter.getSelectors();
// Central Regulator logos
interface CentralRegulatorLogoState extends EntityState<DocumentUrlWithKey> {
    error: any;
    loading: boolean;
}
const centralRegulatorLogoAdapter: EntityAdapter<DocumentUrlWithKey> = createEntityAdapter<DocumentUrlWithKey>({
    selectId: selectKey
});
const centralRegulatorLogoInitialState: CentralRegulatorLogoState = centralRegulatorLogoAdapter.getInitialState({
    error: undefined,
    loading: false
});
const { selectAll: selectAllCentralRegulatorLogos } = centralRegulatorLogoAdapter.getSelectors();

interface CentralRegulatorLogoExtraState extends EntityState<DocumentUrlWithKey> {
    error: any;
    loading: boolean;
}
const centralRegulatorLogoExtraAdapter: EntityAdapter<DocumentUrlWithKey> = createEntityAdapter<DocumentUrlWithKey>({
    selectId: selectKey
});
const centralRegulatorLogoExtraInitialState: CentralRegulatorLogoExtraState = centralRegulatorLogoAdapter.getInitialState({
    error: undefined,
    loading: false
});
const { selectAll: selectAllCentralRegulatorLogoExtras } = centralRegulatorLogoExtraAdapter.getSelectors();

export interface DocumentState {
    documents: DocumentFileState;
    companyLogos: CompanyLogoState;
    companyLogoExtras: CompanyLogoExtraState;
    healthInsurerLogos: HealthInsurerLogoState;
    profilePictures: ProfilePictureState;
    centralRegulatorLogos: CentralRegulatorLogoState;
    centralRegulatorLogoExtras: CentralRegulatorLogoExtraState;
}

export const initialState: DocumentState = {
    documents: documentFileInitialState,
    companyLogos: companyLogoInitialState,
    companyLogoExtras: companyLogoExtraInitialState,
    healthInsurerLogos: healthInsurerLogoInitialState,
    profilePictures: profilePictureInitialState,
    centralRegulatorLogos: centralRegulatorLogoInitialState,
    centralRegulatorLogoExtras: centralRegulatorLogoExtraInitialState
};

export const documentReducer = createReducer(
    initialState,
    on(LoadCompanyLogo, (state, { companyId }) => ({
        ...state,
        companyLogos: companyLogoAdapter.upsertOne(
            {
                key: companyId,
                url: undefined,
                loading: true
            },
            state.companyLogos
        )
    })),
    on(LoadCompanyLogoSuccess, (state, { companyId, companyLogoUrl }) => ({
        ...state,
        companyLogos: companyLogoAdapter.upsertOne(
            {
                key: companyId,
                url: companyLogoUrl,
                loading: false
            },
            state.companyLogos
        )
    })),
    on(LoadCompanyLogoFailure, (state, { companyId, error }) => ({
        ...state,
        error,
        companyLogos: companyLogoAdapter.upsertOne(
            {
                key: companyId,
                url: undefined,
                loading: false
            },
            state.companyLogos
        )
    })),

    on(LoadCompanyLogoExtra, (state, { companyId }) => ({
        ...state,
        companyLogoExtras: companyLogoExtraAdapter.upsertOne(
            {
                key: companyId,
                url: undefined,
                loading: true
            },
            state.companyLogoExtras
        )
    })),
    on(LoadCompanyLogoExtraSuccess, (state, { companyId, companyLogoExtraUrl }) => ({
        ...state,
        companyLogoExtras: companyLogoExtraAdapter.upsertOne(
            {
                key: companyId,
                url: companyLogoExtraUrl,
                loading: false
            },
            state.companyLogoExtras
        )
    })),
    on(LoadCompanyLogoExtraFailure, (state, { companyId, error }) => ({
        ...state,
        error,
        companyLogoExtras: companyLogoExtraAdapter.upsertOne(
            {
                key: companyId,
                url: undefined,
                loading: false
            },
            state.companyLogoExtras
        )
    })),

    on(DeleteCompanyLogo, (state) => ({
        ...state,
        companyLogos: { ...state.companyLogos, loading: true }
    })),

    on(DeleteCompanyLogoSuccess, (state, { companyId }) => ({
        ...state,
        companyLogos: companyLogoAdapter.upsertOne(
            {
                key: companyId,
                url: null,
                loading: false
            },
            state.companyLogos
        )
    })),

    on(DeleteCompanyLogoFailure, (state, { error }) => ({
        ...state,
        error,
        companyLogos: { ...state.companyLogos, loading: false }
    })),

    on(DeleteCompanyLogoExtra, (state) => ({
        ...state,
        companyLogoExtras: { ...state.companyLogoExtras, loading: true }
    })),

    on(DeleteCompanyLogoExtraSuccess, (state, { companyId }) => ({
        ...state,
        companyLogoExtras: companyLogoAdapter.upsertOne(
            {
                key: companyId,
                url: null,
                loading: false
            },
            state.companyLogos
        )
    })),

    on(DeleteCompanyLogoExtraFailure, (state, { error }) => ({
        ...state,
        error,
        companyLogoExtras: { ...state.companyLogoExtras, loading: false }
    })),

    on(LoadHealthInsurerLogo, (state, { healthInsurerId }) => ({
        ...state,
        healthInsurerLogos: healthInsurerLogoAdapter.upsertOne(
            {
                key: healthInsurerId,
                url: undefined,
                loading: true
            },
            state.healthInsurerLogos
        )
    })),

    on(LoadHealthInsurerLogoSuccess, (state, { healthInsurerId, healthInsurerLogoUrl }) => ({
        ...state,
        healthInsurerLogos: healthInsurerLogoAdapter.upsertOne(
            {
                key: healthInsurerId,
                url: healthInsurerLogoUrl,
                loading: false
            },
            state.healthInsurerLogos
        )
    })),
    on(LoadHealthInsurerLogoFailure, (state, { healthInsurerId, error }) => ({
        ...state,
        error,
        healthInsurerLogos: healthInsurerLogoAdapter.upsertOne(
            {
                key: healthInsurerId,
                url: undefined,
                loading: false
            },
            state.healthInsurerLogos
        )
    })),

    on(DeleteHealthInsurerLogo, (state) => ({
        ...state,
        healthInsurerLogos: { ...state.healthInsurerLogos, loading: true }
    })),

    on(DeleteHealthInsurerLogoSuccess, (state, { healthInsurerId }) => ({
        ...state,
        healthInsurerLogos: healthInsurerLogoAdapter.upsertOne(
            {
                key: healthInsurerId,
                url: null,
                loading: false
            },
            state.healthInsurerLogos
        )
    })),

    on(DeleteHealthInsurerLogoFailure, (state, { error }) => ({
        ...state,
        error,
        healthInsurerLogos: { ...state.healthInsurerLogos, loading: false }
    })),

    // already set object in store, to avoid querying multiple times
    on(LoadProfilePicture, (state, { employeeId }) => ({
        ...state,
        profilePictures: profilePictureAdapter.upsertOne(
            {
                key: employeeId,
                url: undefined,
                loading: true
            },
            state.profilePictures
        )
    })),
    on(LoadProfilePictureSuccess, (state, { employeeId, profilePictureUrl }) => ({
        ...state,
        profilePictures: profilePictureAdapter.upsertOne(
            {
                key: employeeId,
                url: profilePictureUrl,
                loading: false
            },
            state.profilePictures
        )
    })),
    on(LoadProfilePictureFailure, (state, { employeeId, error }) => ({
        ...state,
        error,
        profilePictures: profilePictureAdapter.upsertOne(
            {
                key: employeeId,
                url: undefined,
                loading: false
            },
            state.profilePictures
        )
    })),

    on(LoadDocument, (state) => ({
        ...state,
        documents: { ...state.documents, loading: true }
    })),
    on(LoadDocumentSuccess, (state, { document }) => ({
        ...state,
        documents: documentFileAdapter.upsertOne(document, { ...state.documents, loading: false })
    })),
    on(LoadDocumentFailure, (state, { error }) => ({
        ...state,
        error,
        documents: { ...state.documents, loading: true }
    })),
    on(LoadCentralRegulatorLogo, (state, { centralRegulatorId }) => ({
        ...state,
        centralRegulatorLogos: centralRegulatorLogoAdapter.upsertOne(
            {
                key: centralRegulatorId,
                url: undefined,
                loading: true
            },
            state.centralRegulatorLogos
        )
    })),
    on(LoadCentralRegulatorLogoSuccess, (state, { centralRegulatorId, centralRegulatorLogoUrl }) => ({
        ...state,
        centralRegulatorLogos: centralRegulatorLogoAdapter.upsertOne(
            {
                key: centralRegulatorId,
                url: centralRegulatorLogoUrl,
                loading: false
            },
            state.centralRegulatorLogos
        )
    })),
    on(LoadCentralRegulatorLogoFailure, (state, { centralRegulatorId, error }) => ({
        ...state,
        error,
        centralRegulatorLogos: centralRegulatorLogoAdapter.upsertOne(
            {
                key: centralRegulatorId,
                url: undefined,
                loading: false
            },
            state.centralRegulatorLogos
        )
    })),

    on(LoadCentralRegulatorLogoExtra, (state, { centralRegulatorId }) => ({
        ...state,
        centralRegulatorLogoExtras: centralRegulatorLogoExtraAdapter.upsertOne(
            {
                key: centralRegulatorId,
                url: undefined,
                loading: true
            },
            state.centralRegulatorLogoExtras
        )
    })),
    on(LoadCentralRegulatorLogoExtraSuccess, (state, { centralRegulatorId, centralRegulatorLogoExtraUrl }) => ({
        ...state,
        centralRegulatorLogoExtras: centralRegulatorLogoExtraAdapter.upsertOne(
            {
                key: centralRegulatorId,
                url: centralRegulatorLogoExtraUrl,
                loading: false
            },
            state.centralRegulatorLogoExtras
        )
    })),
    on(LoadCentralRegulatorLogoExtraFailure, (state, { centralRegulatorId, error }) => ({
        ...state,
        error,
        centralRegulatorLogoExtras: centralRegulatorLogoExtraAdapter.upsertOne(
            {
                key: centralRegulatorId,
                url: undefined,
                loading: false
            },
            state.centralRegulatorLogoExtras
        )
    })),

    on(DeleteCentralRegulatorLogo, (state) => ({
        ...state,
        centralRegulatorLogos: { ...state.centralRegulatorLogos, loading: true }
    })),

    on(DeleteCentralRegulatorLogoSuccess, (state, { centralRegulatorId }) => ({
        ...state,
        centralRegulatorLogos: centralRegulatorLogoAdapter.upsertOne(
            {
                key: centralRegulatorId,
                url: null,
                loading: false
            },
            state.centralRegulatorLogos
        )
    })),

    on(DeleteCentralRegulatorLogoFailure, (state, { error }) => ({
        ...state,
        error,
        centralRegulatorLogos: { ...state.centralRegulatorLogos, loading: false }
    })),

    on(DeleteCentralRegulatorLogoExtra, (state) => ({
        ...state,
        centralRegulatorLogoExtras: { ...state.centralRegulatorLogoExtras, loading: true }
    })),

    on(DeleteCentralRegulatorLogoExtraSuccess, (state, { centralRegulatorId }) => ({
        ...state,
        centralRegulatorLogoExtras: centralRegulatorLogoAdapter.upsertOne(
            {
                key: centralRegulatorId,
                url: null,
                loading: false
            },
            state.centralRegulatorLogos
        )
    })),

    on(DeleteCentralRegulatorLogoExtraFailure, (state, { error }) => ({
        ...state,
        error,
        centralRegulatorLogoExtras: { ...state.centralRegulatorLogoExtras, loading: false }
    }))
);

export const documentState = (state: AppState) => state.documentState;

export const documentFileState = createSelector(documentState, (state) => state.documents);
export const allDocuments = createSelector(documentFileState, selectAllDocuments);
export const documentLoading = createSelector(documentFileState, (state) => state.loading);
export const documentByDocumentDescriptionId = (documentDescriptionId: string) =>
    createSelector(allDocuments, (state) => state.find((doc) => doc.documentDescriptionId === documentDescriptionId));
export const documentsByDocumentDescriptionIds = (ids: string[]) =>
    createSelector(allDocuments, (state) => state.filter((doc) => ids.includes(doc.documentDescriptionId)));

export const companyLogoState = createSelector(documentState, (state) => state.companyLogos);
export const companyLogoExtraState = createSelector(documentState, (state) => state.companyLogoExtras);

export const allCompanyLogos = createSelector(companyLogoState, selectAllCompanyLogos);
export const companyLogoLoading = createSelector(companyLogoState, ({ loading }) => loading);
export const companyLogoById = (companyId: string) =>
    createSelector(allCompanyLogos, (state) => state.find((elem) => elem.key === companyId));

export const allCompanyLogoExtras = createSelector(companyLogoExtraState, selectAllCompanyLogoExtras);
export const companyLogoExtraLoading = createSelector(companyLogoExtraState, ({ loading }) => loading);
export const companyLogoExtraById = (companyId: string) =>
    createSelector(allCompanyLogoExtras, (state) => state.find((elem) => elem.key === companyId));

export const healthInsurerLogoState = createSelector(documentState, (state) => state.healthInsurerLogos);
export const allHealthInsurerLogos = createSelector(healthInsurerLogoState, selectAllHealthInsurerLogos);
export const healthInsurerLogoById = (healthInsurerId: string) =>
    createSelector(allHealthInsurerLogos, (state) => state.find((elem) => elem.key === healthInsurerId));

export const profilePictureState = createSelector(documentState, (state) => state.profilePictures);
export const allProfilePictures = createSelector(profilePictureState, selectAllProfilePictures);
export const profilePictureById = (employeeId: string) =>
    createSelector(allProfilePictures, (state) => state.find((elem) => elem.key === employeeId));

export const centralRegulatorLogoState = createSelector(documentState, (state) => state.centralRegulatorLogos);
export const centralRegulatorLogoExtraState = createSelector(documentState, (state) => state.centralRegulatorLogoExtras);

export const allCentralRegulatorLogos = createSelector(centralRegulatorLogoState, selectAllCentralRegulatorLogos);
export const centralRegulatorLogoLoading = createSelector(centralRegulatorLogoState, ({ loading }) => loading);
export const centralRegulatorLogoById = (centralRegulatorId: string) =>
    createSelector(allCentralRegulatorLogos, (state) => state.find((elem) => elem.key === centralRegulatorId));

export const allCentralRegulatorLogoExtras = createSelector(centralRegulatorLogoExtraState, selectAllCentralRegulatorLogoExtras);
export const centralRegulatorLogoExtraLoading = createSelector(centralRegulatorLogoExtraState, ({ loading }) => loading);
export const centralRegulatorLogoExtraById = (centralRegulatorId: string) =>
    createSelector(allCentralRegulatorLogoExtras, (state) => state.find((elem) => elem.key === centralRegulatorId));
